"use client";

import BulletListExtension from "@tiptap/extension-bullet-list";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Link from "@tiptap/extension-link";
import ListItem from "@tiptap/extension-list-item";
import OrderedListExtension from "@tiptap/extension-ordered-list";
import Placeholder from "@tiptap/extension-placeholder";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import { TableRow } from "@tiptap/extension-table-row";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";

import {
	CustomHorizontalRule,
	CustomImage,
	CustomTable,
	CustomTableCell,
	CustomTableHeader,
	Div,
	FlexBoxNode,
	FontFamily,
	FontSize,
	FontWeight,
	GlobalAttributes,
	GridBoxNode,
	InputRadio,
	LabelNode,
	ResponsiveClass,
	ResponsiveStyleSpan,
	StyleTag,
} from "../extensions";
import type { SelectionState } from "../types";
import { wrapImagesWithLinks } from "../utils";
import { normalizeTablesInDocument, normalizeTablesInHTML } from "../utils/tableSanitizer";
import { generateCSSForAllTables } from "./helpers/tableCSSGenerators";

/**
 * TipTap's toggleList replaces the parent list type (whole &lt;ul&gt; becomes &lt;ol&gt;).
 * For nested mixed lists inside one &lt;li&gt;, wrap the current block(s) in the other list instead.
 */
const OrderedList = OrderedListExtension.extend({
	addCommands() {
		return {
			toggleOrderedList:
				() =>
					({ editor, commands }) => {
						if (editor.isActive("bulletList") && !editor.isActive("orderedList")) {
							if (commands.wrapInList("orderedList")) {
								return true;
							}
						}
						return commands.toggleList("orderedList", "listItem", false);
					},
		};
	},
});

const BulletList = BulletListExtension.extend({
	addCommands() {
		return {
			toggleBulletList:
				() =>
					({ editor, commands }) => {
						if (editor.isActive("orderedList") && !editor.isActive("bulletList")) {
							if (commands.wrapInList("bulletList")) {
								return true;
							}
						}
						return commands.toggleList("bulletList", "listItem", false);
					},
		};
	},
});

/**
 * Extracts and preserves non-table CSS rules from HTML style tags.
 * DOMParser moves style tags to <head>, so we need to extract them first.
 * This handles mixed style tags that contain both table CSS and non-table CSS.
 */
function extractNonTableStyleTags(html: string): { cleanHtml: string; preservedStyles: string[] } {
	const preservedCssRules: string[] = [];

	// Extract all style tags
	const styleRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
	const cleanHtml = html.replace(styleRegex, (match, cssContent) => {
		// If this is a dedicated table style tag, skip it entirely (will be regenerated)
		if (match.includes('data-table-style')) {
			return '';
		}

		// Separate arrays for base rules and media queries to maintain correct CSS order
		const baseRules: string[] = [];
		const mediaQueryRules: string[] = [];

		// Media query patterns for responsive classes (unified + legacy)
		const mediaQueryPatterns = [
			/@media\s*\([^)]+\)\s*\{\s*\.responsive-style-[^\s{]+\s*\{[^}]*\}\s*\}/g,
			/@media\s*\([^)]+\)\s*\{\s*\.responsive-font-[^\s{]+\s*\{[^}]*\}\s*\}/g,
			/@media\s*\([^)]+\)\s*\{\s*\.responsive-weight-[^\s{]+\s*\{[^}]*\}\s*\}/g,
			/@media\s*\([^)]+\)\s*\{\s*\.responsive-align-[^\s{]+\s*\{[^}]*\}\s*\}/g,
		];

		// Extract media query rules first and remove them from content
		let contentWithoutMediaQueries = cssContent;
		for (const pattern of mediaQueryPatterns) {
			const matches = cssContent.match(pattern);
			if (matches) {
				mediaQueryRules.push(...matches);
				// Remove matched media queries from content so base rule extraction doesn't match inside them
				matches.forEach((m: string) => {
					contentWithoutMediaQueries = contentWithoutMediaQueries.replace(m, '');
				});
			}
		}

		// Now extract base rules (without media queries) from the remaining content
		const nonTableBasePatterns = [
			/\.responsive-style-[^\s{]+\s*\{[^}]*\}/g,
			/\.responsive-font-[^\s{]+\s*\{[^}]*\}/g,
			/\.responsive-weight-[^\s{]+\s*\{[^}]*\}/g,
			/\.responsive-align-[^\s{]+\s*\{[^}]*\}/g,
		];

		for (const pattern of nonTableBasePatterns) {
			const matches = contentWithoutMediaQueries.match(pattern);
			if (matches) {
				baseRules.push(...matches);
			}
		}

		// IMPORTANT: Add base rules FIRST, then media queries
		// This ensures proper CSS cascade (base styles, then responsive overrides)
		preservedCssRules.push(...baseRules, ...mediaQueryRules);

		// Remove the entire style tag (table CSS will be regenerated, non-table CSS preserved separately)
		return '';
	});

	// Create a single style tag for all preserved CSS rules
	const preservedStyles: string[] = [];
	if (preservedCssRules.length > 0) {
		// Deduplicate CSS rules
		const uniqueRules = [...new Set(preservedCssRules)];
		preservedStyles.push(`<style>${uniqueRules.join('\n')}</style>`);
	}

	return { cleanHtml, preservedStyles };
}

/**
 * Ensures table styles are properly maintained while preserving non-table styles.
 * This function:
 * 1. Preserves non-table style tags (responsive font, weight, align styles)
 * 2. Cleans up wrapper attributes (data-for-table, data-table-wrapper)
 * 3. Removes inline col styles that TipTap adds
 * 4. Removes w-full class from tables
 * 5. Regenerates CSS for tables if missing
 */
function ensureTableStylesExist(html: string): string {
	// Extract non-table style tags BEFORE DOMParser (it moves styles to <head>)
	const { cleanHtml: htmlWithoutStyles, preservedStyles } = extractNonTableStyleTags(html);

	// Quick check - if no tables, just return with preserved styles.
	// Note: this tests for a table element rather than data-table-id, because a
	// freshly pasted table has no id yet and still needs normalising.
	if (!htmlWithoutStyles.includes('<table')) {
		// Prepend preserved non-table styles and return
		return preservedStyles.join('') + htmlWithoutStyles;
	}

	const parser = new DOMParser();
	const doc = parser.parseFromString(htmlWithoutStyles, "text/html");

	// Repair anything that does not look like a table this editor built — pasted
	// tables, tables from older versions, and the attribute-less cells TipTap
	// creates for a new row or column. The original html is passed as the CSS
	// source because the <style> tags were pulled out above.
	normalizeTablesInDocument(doc.body, { cssSource: html });

	// Get all table IDs
	const tableIds = new Set<string>();
	doc.querySelectorAll("table[data-table-id]").forEach((table) => {
		const id = table.getAttribute("data-table-id");
		if (id) tableIds.add(id);
	});

	if (tableIds.size === 0) {
		return preservedStyles.join('') + htmlWithoutStyles;
	}

	let needsRegeneration = false;

	// Process each table
	doc.querySelectorAll("table[data-table-id]").forEach((table) => {
		const tableEl = table as HTMLTableElement;
		const tableId = tableEl.getAttribute("data-table-id");
		if (!tableId) return;

		// 1. Ensure wrapper has proper attributes
		const wrapper = tableEl.closest(".table-wrapper");
		if (wrapper) {
			const wrapperForTable = wrapper.getAttribute("data-for-table");
			if (wrapperForTable !== tableId) {
				wrapper.setAttribute("data-for-table", tableId);
				wrapper.setAttribute("data-table-wrapper", "true");
				needsRegeneration = true;
			}
		}

		// 2. Clean up col inline styles (TipTap adds these from colwidth attributes)
		tableEl.querySelectorAll("col").forEach((col) => {
			const colEl = col as HTMLElement;
			colEl.style.removeProperty("min-width");
			colEl.style.removeProperty("width");
			colEl.style.removeProperty("max-width");
			const colStyle = colEl.getAttribute("style");
			if (!colStyle || colStyle.trim() === "") {
				colEl.removeAttribute("style");
			}
		});

		// 3. Remove w-full class (it overrides responsive styles)
		const currentClass = tableEl.getAttribute("class") || "";
		if (currentClass.includes("w-full")) {
			tableEl.setAttribute("class", currentClass.replace(/\bw-full\b/g, "").replace(/\s+/g, " ").trim());
			needsRegeneration = true;
		}

		// 4. Clean up or restore table min-width based on data attributes
		const widthXs = tableEl.getAttribute("data-width-xs");
		if (widthXs && widthXs.startsWith("pixel:")) {
			const [, value] = widthXs.split(":");
			if (value) {
				tableEl.style.width = `${value}px`;
				tableEl.style.minWidth = `${value}px`;
				tableEl.style.maxWidth = `${value}px`;
			}
		} else {
			const currentMinWidth = tableEl.style.minWidth;
			if (currentMinWidth && !widthXs?.startsWith("pixel:")) {
				const minWidthPx = parseInt(currentMinWidth);
				const dataPxValue = widthXs ? parseInt(widthXs.split(":")[1] || "0") : 0;
				if (minWidthPx !== dataPxValue && minWidthPx > 0) {
					tableEl.style.removeProperty("min-width");
					needsRegeneration = true;
				}
			}
		}
	});

	// Check if any tables need style regeneration (missing style tags)
	// Note: Style tags were already removed, so always regenerate if tables exist
	needsRegeneration = true;

	let resultHtml = doc.body.innerHTML;

	// Regenerate CSS for all tables
	if (needsRegeneration && tableIds.size > 0) {
		const allStyleTags = generateCSSForAllTables(resultHtml);
		if (allStyleTags) {
			resultHtml = resultHtml + allStyleTags;
		}
	}

	// Prepend preserved non-table style tags
	if (preservedStyles.length > 0) {
		resultHtml = preservedStyles.join('') + resultHtml;
	}

	return resultHtml;
}

const INLINE_TAGS = new Set(["span", "a", "b", "i", "u", "s", "em", "strong", "sub", "sup", "mark", "small", "del", "ins"]);
const BLOCK_TEXT_TAGS = new Set(["p", "h1", "h2", "h3", "h4", "h5", "h6", "blockquote", "li"]);

/**
 * Sanitizes a DOM element by removing unwanted styles and classes, especially those from Word/Google Docs.
 * This is a best-effort sanitization to clean up common issues from pasted content.
 *
 * `pasted` controls how aggressive the pass is:
 * - always: Mso* styles and classes, and Google Docs' meaningless bold wrapper
 * - pasted only: background styles, white-space on inline elements, and
 *   margin/padding on block text elements
 *
 * The aggressive rules must not run on the editor's own output — it runs on
 * every update, and stripping backgrounds there wiped cell background colours
 * (and any other background the user set) out of the saved HTML.
 */
function sanitizeDOMElement(root: Element, pasted = false): void {
	root.querySelectorAll("*").forEach((el) => {
		const tagName = el.tagName.toLowerCase();
		const style = el.getAttribute("style");
		if (style) {
			const filteredStyle = style
				.split(";")
				.map((prop) => prop.trim())
				.filter((prop) => {
					if (!prop) return false;
					const colonIdx = prop.indexOf(":");
					if (colonIdx === -1) return false;
					const propName = prop.slice(0, colonIdx).trim().toLowerCase();
					if (propName.startsWith("mso-")) return false;
					if (!pasted) return true;
					if (propName === "background" || propName === "background-color") return false;
					if (propName === "white-space" && INLINE_TAGS.has(tagName)) return false;
					if ((propName.startsWith("margin") || propName.startsWith("padding")) && BLOCK_TEXT_TAGS.has(tagName)) return false;
					return true;
				})
				.join("; ")
				.trim();
			if (filteredStyle) {
				el.setAttribute("style", filteredStyle);
			} else {
				el.removeAttribute("style");
			}
		}

		const className = el.getAttribute("class");
		if (className) {
			const filteredClass = className
				.split(/\s+/)
				.filter((cls) => !cls.startsWith("Mso") && !cls.startsWith("mso"))
				.join(" ")
				.trim();
			if (filteredClass) {
				el.setAttribute("class", filteredClass);
			} else {
				el.removeAttribute("class");
			}
		}
	});

	// Unwrap Google Docs <b style="font-weight:normal"> — used to suppress heading bold, meaningless otherwise
	root.querySelectorAll("b, strong").forEach((el) => {
		const style = el.getAttribute("style") ?? "";
		const fwMatch = style.match(/font-weight\s*:\s*([^;]+)/i);
		if (fwMatch) {
			const fw = fwMatch[1].trim().toLowerCase();
			if (fw === "normal" || fw === "400") {
				const parent = el.parentNode;
				if (parent) {
					while (el.firstChild) parent.insertBefore(el.firstChild, el);
					parent.removeChild(el);
				}
			}
		}
	});
}

/**
 * Sanitizes HTML content by removing Mso* styles and classes and unwrapping
 * Google Docs' meaningless <b style="font-weight:normal"> wrapper.
 *
 * This runs on the editor's own output on every update, so it deliberately
 * leaves the author's styling alone; the aggressive paste-only rules live in
 * {@link sanitizeDOMElement}.
 */
export function sanitizeHTML(html: string): string {
	if (!html) return html;
	const parser = new DOMParser();
	const doc = parser.parseFromString(html, "text/html");
	sanitizeDOMElement(doc.body);
	return doc.body.innerHTML;
}

interface UseEditorSetupProps {
	value: string;
	placeholder: string;
	onChange?: (html: string) => void;
	onSelectionStateChange: (updater: (prev: SelectionState) => SelectionState) => void;
	isInternalChangeRef: React.MutableRefObject<boolean>;
}

export const useEditorSetup = ({
	value,
	placeholder,
	onChange,
	onSelectionStateChange,
	isInternalChangeRef,
}: UseEditorSetupProps) => {
	const editor = useEditor({
		immediatelyRender: false,
		extensions: [
			StarterKit.configure({
				listItem: false,
				bulletList: false,
				orderedList: false,
				horizontalRule: false,
			}),
			TextStyle,
			Color,
			FontFamily,
			Highlight.configure({ multicolor: true }),
			Subscript,
			Superscript,
			TextAlign.configure({ types: ["heading", "paragraph"] }),
			Placeholder.configure({ placeholder }),
			Link.configure({
				openOnClick: false,
				autolink: true,
				linkOnPaste: true,
				HTMLAttributes: {
					class: "text-blue-600 cursor-pointer",
					rel: "",
				},
			}),
			CustomImage.configure({
				inline: false,
				allowBase64: true,
				HTMLAttributes: { class: "max-w-full h-auto rounded-lg cursor-pointer" },
			}),
			CustomHorizontalRule,
			GlobalAttributes,
			FlexBoxNode,
			GridBoxNode,
			Div,
			StyleTag,
			LabelNode,
			InputRadio,
			FontSize,
			FontWeight,
			ListItem,
			BulletList,
			OrderedList,
			Underline,
			ResponsiveClass,
			ResponsiveStyleSpan,
			CustomTable.configure({
				resizable: true,
				HTMLAttributes: { class: "border-collapse w-full" },
			}),
			TableRow,
			CustomTableHeader,
			CustomTableCell,
		],
		// Normalise before parsing so existing content is repaired inside the
		// document itself, not just in the HTML handed back through onChange.
		content: normalizeTablesInHTML(sanitizeHTML(value)),
		onCreate: ({ editor }) => {
			// Sync the processed content on initial load to get accurate size calculation
			isInternalChangeRef.current = true;
			const html = editor.getHTML();
			let processedHtml = sanitizeHTML(wrapImagesWithLinks(html));
			processedHtml = ensureTableStylesExist(processedHtml);
			onChange?.(processedHtml);
			setTimeout(() => {
				isInternalChangeRef.current = false;
			}, 100);
		},
		onUpdate: ({ editor }) => {
			isInternalChangeRef.current = true;
			const html = editor.getHTML();
			let processedHtml = sanitizeHTML(wrapImagesWithLinks(html));
			processedHtml = ensureTableStylesExist(processedHtml);
			onChange?.(processedHtml);
			setTimeout(() => {
				isInternalChangeRef.current = false;
			}, 100);
		},
		onSelectionUpdate: ({ editor }) => {
			const inTable = editor.isActive("table");
			if (inTable) {
				const canMerge = editor.can().mergeCells();
				const canSplit = editor.can().splitCell();
				onSelectionStateChange((prev) => {
					if (prev.canMergeCells === canMerge && prev.canSplitCell === canSplit) {
						return prev;
					}
					return { ...prev, canMergeCells: canMerge, canSplitCell: canSplit };
				});
			}
		},
		editorProps: {
			attributes: {
				class:
					"prose prose-sm sm:prose lg:prose-lg xl:prose-xl max-w-none p-4 focus:outline-none min-h-[300px]",
			},
			// Prevent backspace/delete from removing the first block element at position 0
			handleKeyDown: (view, event) => {
				const { state } = view;
				const { selection, doc } = state;
				const { from, empty } = selection;

				// Only handle backspace and delete keys
				if (event.key !== 'Backspace' && event.key !== 'Delete') {
					return false;
				}

				// Check if cursor is at the very beginning of the document (position 1 = start of first block content)
				if (from <= 1 && empty && event.key === 'Backspace') {
					// At document start, prevent backspace from deleting the first block
					return true; // Prevent default
				}

				// Check if cursor is at position 0 within the first content block
				const $from = selection.$from;

				// If at the start of a block that is the first content block (ignoring style tags)
				if ($from.parentOffset === 0 && event.key === 'Backspace') {
					// Find the first non-styleTag block
					let firstNonStyleBlockPos = -1;
					doc.forEach((node, offset) => {
						if (firstNonStyleBlockPos === -1 && node.type.name !== 'styleTag') {
							firstNonStyleBlockPos = offset;
						}
					});

					// If we're at the start of the first content block, prevent deletion
					if (firstNonStyleBlockPos !== -1 && from === firstNonStyleBlockPos + 1) {
						return true; // Prevent backspace at start of first content block
					}
				}

				return false; // Let other handlers process
			},
			clipboardTextSerializer: (content) => {
				const slice = content.content;
				let text = "";
				slice.forEach((node) => {
					if (node.type.name === "styleTag") return;
					if (node.textContent) text += node.textContent + "\n";
				});
				return text.trim();
			},
			// Sanitize and fix table IDs when content is pasted
			transformPastedHTML: (html) => {
				const parser = new DOMParser();
				const doc = parser.parseFromString(html, "text/html");

				// Strip Word/Google Docs/LibreOffice/web-paste noise (single DOM pass)
				sanitizeDOMElement(doc.body, true);

				const tables = doc.querySelectorAll("table[data-table-id]");
				const oldToNewIdMap = new Map<string, string>();

				tables.forEach((table) => {
					const oldId = table.getAttribute("data-table-id");
					if (oldId) {
						const newId = `table-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
						oldToNewIdMap.set(oldId, newId);
						table.setAttribute("data-table-id", newId);
					}
				});

				// Update wrapper references
				const wrappers = doc.querySelectorAll("[data-for-table]");
				wrappers.forEach((wrapper) => {
					const oldId = wrapper.getAttribute("data-for-table");
					if (oldId && oldToNewIdMap.has(oldId)) {
						wrapper.setAttribute("data-for-table", oldToNewIdMap.get(oldId)!);
					}
				});

				// Drop stylesheets copied from foreign pages. They target classes that
				// are stripped below anyway, and their global rules (p, td, ...) would
				// leak into the whole document once inserted as a styleTag node.
				doc.querySelectorAll("style").forEach((style) => {
					const css = style.textContent || "";
					const isOurs =
						style.hasAttribute("data-table-style") ||
						/table\[data-table-id|\.cell-style-|\.col-style-|\.table-style-|\.responsive-/.test(css);
					if (!isOurs) {
						style.remove();
						return;
					}
					// Remove !important from pasted <style> elements so inline styles
					// applied after paste are not silently overridden by copied CSS rules.
					style.textContent = css.replace(/\s*!important/gi, "");
				});

				// Update style tags
				const styleTags = doc.querySelectorAll("style[data-table-style]");
				styleTags.forEach((style) => {
					const oldId = style.getAttribute("data-table-style");
					if (oldId && oldToNewIdMap.has(oldId)) {
						const newId = oldToNewIdMap.get(oldId)!;
						style.setAttribute("data-table-style", newId);
						// Replace old ID with new ID in the CSS content
						style.textContent = style.textContent?.replace(new RegExp(oldId, 'g'), newId) || "";
					}
				});

				// Give pasted tables the shape our toolbar builds: an id, a wrapper,
				// cell borders, column indexes, and none of the source's own classes.
				normalizeTablesInDocument(doc.body, { cssSource: html });

				return doc.body.innerHTML;
			},
		},
	});

	return editor;
};

